import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Typography,
  Grid,
  Link as MuiLink,
} from "@mui/material";
import { Publish, AttachFile } from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { ThinLayout } from "../Layouts/ThinLayout";
import { BlingCheckmark } from "../Components/BlingCheckmark";
import { FileAttachment } from "../Components/FileUpload/FileAttachment";
import { DocumentViewer } from "../Components/SubmissionReview/DocumentViewer";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_SUBMISSION_REVIEW_SUBMISSION,
  SUBMIT_SUBMISSION_REVIEW_SUBMISSION,
  SAVE_SUBMISSION_DRAFT,
  GET_SUBMISSION_REVIEW_UPLOAD_URL,
  CONFIRM_SUBMISSION_REVIEW_UPLOAD,
} from "../Queries";
import { useErrorBoundary } from "../Contexts/ErrorContext";
import { extractErrorMessage } from "../utils/errorUtils";
import { useTranslation } from "react-i18next";

export function StudentSubmissionPage() {
  const [searchParams] = useSearchParams();
  const { setError } = useErrorBoundary();
  const [t] = useTranslation();
  const [submissionText, setSubmissionText] = useState("");
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [fileUploading, setFileUploading] = useState(false);
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);

  // Decode token from query params
  const key = searchParams.get("key");
  let submissionId = "";
  let submissionToken = "";
  try {
    const decoded = atob(key || "");
    const parts = decoded.split(":");
    submissionId = parts[0];
    submissionToken = parts.slice(1).join(":");
  } catch {
    // Invalid key
  }

  const authHeaders = {
    headers: { Authorization: `JWT ${submissionToken}` },
  };

  const { loading, data } = useQuery(GET_SUBMISSION_REVIEW_SUBMISSION, {
    variables: { pk: submissionId },
    context: authHeaders,
    skip: !submissionId,
    onCompleted: (result) => {
      if (result?.submissionReviewSubmissionById?.submissionText) {
        setSubmissionText(result.submissionReviewSubmissionById.submissionText);
      }
    },
    onError: (error) =>
      setError(
        extractErrorMessage(error, String(t("Error loading submission"))),
        error,
      ),
  });

  const [submitMutation] = useMutation(SUBMIT_SUBMISSION_REVIEW_SUBMISSION, {
    context: authHeaders,
    refetchQueries: [
      {
        query: GET_SUBMISSION_REVIEW_SUBMISSION,
        variables: { pk: submissionId },
        context: authHeaders,
      },
    ],
    onError: (error) =>
      setError(
        extractErrorMessage(error, String(t("Error submitting"))),
        error,
      ),
  });

  const [saveDraftMutation] = useMutation(SAVE_SUBMISSION_DRAFT, {
    context: authHeaders,
    onError: (error) =>
      setError(
        extractErrorMessage(error, String(t("Error saving draft"))),
        error,
      ),
  });

  const [getUploadUrl] = useMutation(GET_SUBMISSION_REVIEW_UPLOAD_URL, {
    context: authHeaders,
    onError: (error) => {
      setFileUploading(false);
      setFileUploadError(error.message);
    },
  });

  const [confirmUpload] = useMutation(CONFIRM_SUBMISSION_REVIEW_UPLOAD, {
    context: authHeaders,
    onCompleted: () => {
      setFileUploading(false);
      setFileUploadError(null);
    },
    onError: (error) => {
      setFileUploading(false);
      setFileUploadError(error.message);
    },
  });

  const submission = data?.submissionReviewSubmissionById;

  if (loading) {
    return (
      <ThinLayout title={String(t("Loading..."))} loading={true}>
        <div />
      </ThinLayout>
    );
  }

  if (!submission) {
    return (
      <ThinLayout title={String(t("Error"))}>
        <Typography variant="h5">{t("Submission not found")}</Typography>
      </ThinLayout>
    );
  }

  // Already submitted — show confirmation
  if (submission.status === "SB") {
    return (
      <ThinLayout title="">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
          }}
        >
          <Box sx={{ textAlign: "center", maxWidth: 600 }}>
            <Typography variant="h4" sx={{ mb: 2 }}>
              {t("You're all done! Your submission has been received.")}
            </Typography>
            <BlingCheckmark
              active={false}
              completed={true}
              size="large"
              divProps={{ style: { marginTop: 20 } }}
            />
            {(submission.submissionText || submission.submissionFileUrl) && (
              <Card sx={{ mt: 4, textAlign: "left" }}>
                <CardHeader title={String(t("Your Submission"))} />
                <CardContent>
                  {submission.submissionText && (
                    <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                      {submission.submissionText}
                    </Typography>
                  )}
                  {/* Show the attached file so students can confirm what
                      was actually submitted */}
                  {submission.submissionFileUrl && (
                    <Box
                      sx={{
                        mt: submission.submissionText ? 2 : 0,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <AttachFile fontSize="small" color="action" />
                      <MuiLink
                        href={submission.submissionFileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t("View your attached file")}
                      </MuiLink>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
          </Box>
        </Box>
      </ThinLayout>
    );
  }

  const handleSubmit = () => {
    submitMutation({
      variables: {
        submissionId: submission.id,
        submissionText,
      },
    });
  };

  const handleGetUploadUrl = async (filename: string, fileSize: number) => {
    setFileUploading(true);
    setFileUploadError(null);
    const result = await getUploadUrl({
      variables: { submissionId: submission.id, filename, fileSize },
    });
    return result.data?.getSubmissionReviewUploadUrl;
  };

  const handleConfirmUpload = async (
    fileUrl: string,
    originalFilename: string,
  ) => {
    await confirmUpload({
      variables: { submissionId: submission.id, fileUrl, originalFilename },
    });
  };

  const hasFile = attachedFile !== null || submission?.submissionFileUrl;
  const hasText = submissionText.trim().length > 0;
  const submissionType = submission?.activitySubmissionType;

  // Format-specific validation
  const canSubmit = (() => {
    if (submissionType === "DOC" || submissionType === "VID") {
      return hasFile; // Document/Video requires file
    }
    if (submissionType === "TXT") {
      return hasText; // Text requires text
    }
    if (submissionType === "LNK") {
      return hasText; // Link requires URL in text field
    }
    return hasText || hasFile; // Default: either
  })();

  const isOverdue = Boolean(
    submission.activityDueDate &&
      new Date() > new Date(submission.activityDueDate),
  );
  const isClosed =
    submission.activityStatus !== "SO" ||
    Boolean(
      submission.activityCloseDate &&
        new Date() > new Date(submission.activityCloseDate),
    );

  return (
    <ThinLayout
      title={submission.activityName || String(t("Submit Your Work"))}
      fullWidth={true}
    >
      <Box sx={{ p: 3 }}>
        {/* Due date warning */}
        {isOverdue && !isClosed && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t("This submission is past due, but you can still submit.")}
          </Alert>
        )}
        {isClosed && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t(
              "Submissions are closed. You can no longer submit or modify your work.",
            )}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Left: Instructions + Reference documentation */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                {/* Activity instructions */}
                {submission.activityDescription && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 1, fontWeight: "bold" }}
                    >
                      {t("Instructions")}
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                      {submission.activityDescription}
                    </Typography>
                  </Box>
                )}

                {/* Due date display */}
                {submission.activityDueDate && (
                  <Typography
                    variant="body2"
                    color={isOverdue ? "error" : "text.secondary"}
                    sx={{ mb: 2 }}
                  >
                    {t("Due")}:{" "}
                    {new Date(submission.activityDueDate).toLocaleDateString()}{" "}
                    {t("at")}{" "}
                    {new Date(submission.activityDueDate).toLocaleTimeString()}
                    {isOverdue && ` (${String(t("OVERDUE"))})`}
                  </Typography>
                )}

                {/* Reference documentation (instructor's attached file) */}
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 1, fontWeight: "bold" }}
                >
                  {t("Reference Documentation")}
                </Typography>
                {submission.activityPdfAttachmentUrl ? (
                  <DocumentViewer
                    fileUrl={submission.activityPdfAttachmentUrl}
                  />
                ) : (
                  <Typography color="text.secondary">
                    {t("No reference documentation provided.")}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Right: Submission form */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title={String(t("Your Submission"))} />
              <CardContent>
                <TextField
                  label={String(t("Submission Text"))}
                  placeholder={String(t("Enter your submission here..."))}
                  multiline
                  rows={8}
                  fullWidth
                  value={submissionText}
                  onChange={(e) => setSubmissionText(e.target.value)}
                  disabled={isClosed}
                  sx={{ mb: 3 }}
                />

                <FileAttachment
                  onFileChange={setAttachedFile}
                  onGetUploadUrl={handleGetUploadUrl}
                  onConfirmUpload={handleConfirmUpload}
                  onUploadError={(message) => {
                    setFileUploading(false);
                    setFileUploadError(message);
                  }}
                  disabled={isClosed}
                  acceptedFileTypes=".pdf,.doc,.docx,.txt,.ppt,.pptx"
                  maxFileSize={10}
                  uploading={fileUploading}
                  uploadError={fileUploadError}
                  existingFileUrl={submission?.submissionFileUrl}
                  autoUpload={true}
                />

                <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                  {!isClosed && (
                    <>
                      <Button
                        variant="outlined"
                        onClick={() =>
                          saveDraftMutation({
                            variables: {
                              submissionId: submission.id,
                              submissionText,
                            },
                          })
                        }
                        disabled={!submissionText.trim()}
                      >
                        {t("Save Draft")}
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        startIcon={<Publish />}
                      >
                        {t("Submit")}
                      </Button>
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </ThinLayout>
  );
}
